import { toastManager } from "@circleco/compass/context/CompassContext";
import type { QueryClientConfig, QueryKey } from "react-query";
import { MutationCache, QueryCache, QueryClient } from "react-query";
import invariant from "tiny-invariant";

import { t } from "@/i18n-js/instance";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";

interface DefaultQueryFnParams {
  queryKey: QueryKey;
}

const NON_RETRY_STATUS_CODES = new Set([401, 403, 404]);
const MAX_QUERY_RETRY_COUNT = 3;

// https://react-query.tanstack.com/guides/default-query-function
const defaultQueryFn = ({ queryKey }: DefaultQueryFnParams) => {
  if (Array.isArray(queryKey)) {
    return reactQueryGet(queryKey[0]);
  }

  if (typeof queryKey === "string") {
    return reactQueryGet(queryKey);
  }

  return undefined;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof error.status === "number"
  ) {
    return error.status;
  }

  return undefined;
};

const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const status = getErrorStatus(error);

  if (status !== undefined && NON_RETRY_STATUS_CODES.has(status)) {
    return false;
  }

  return failureCount < MAX_QUERY_RETRY_COUNT;
};

type ErrorMessageResolver = (error: unknown) => string;

const isErrorMessageResolver = (
  errorMessage: unknown,
): errorMessage is ErrorMessageResolver => typeof errorMessage === "function";

const resolveErrorMessage = (
  error: unknown,
  errorMessage: unknown,
): string | undefined => {
  if (!errorMessage) return;

  const resolvedErrorMessage = isErrorMessageResolver(errorMessage)
    ? errorMessage(error)
    : errorMessage;

  invariant(
    typeof resolvedErrorMessage === "string",
    "errorMessage must resolve to a string",
  );

  return resolvedErrorMessage;
};

const createQueryCache = () =>
  new QueryCache({
    onError: (error, query) => {
      const errorMessage = resolveErrorMessage(error, query.meta?.errorMessage);
      if (!errorMessage) return;

      toastManager.add({
        title: errorMessage,
        variant: "destructive",
        icon: "triangle-exclamation-filled",
        iconColor: "danger",
      });
    },
  });

export const createQueryConfig = (): QueryClientConfig => ({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
  },
  mutationCache: new MutationCache({
    onError: (data: unknown) => {
      if (
        typeof data === "object" &&
        data !== null &&
        "body" in data &&
        typeof data.body === "object" &&
        data.body !== null &&
        "view_only_masquerading" in data.body &&
        data.body.view_only_masquerading
      ) {
        if (typeof window.onViewOnlyModeError === "function") {
          window.onViewOnlyModeError(t("view_only_error"));
        }
        throw new Error(t("view_only_error"));
      }
    },
  }),
  queryCache: createQueryCache(),
});

export const queryClient = new QueryClient(createQueryConfig());
